import { Component, Input, computed, inject, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ModulesService } from '@shared/services/modules.service';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * Encabezado homologado de las pantallas: SOLO la etiqueta visible del
 * módulo, centrada, sin descripción. Si la pantalla está dentro de un menú
 * (Auditoría, Soporte, Salida de material…) muestra la flecha de regreso.
 *
 * Los botones de acción (Crear, Actualizar, Descargar…) NO van aquí: van en
 * la barra `.list-actions`, arriba de la tabla y debajo de los filtros.
 */
@Component({
  selector: 'app-page-title',
  standalone: true,
  imports: [MatIconModule, MatButtonModule, MatTooltipModule],
  template: `
    <div class="page-title">
      @if (backRoute) {
        <button mat-icon-button class="page-title__back" type="button"
                [matTooltip]="backTooltip" (click)="back()">
          <mat-icon>arrow_back</mat-icon>
        </button>
      }
      <h2 class="page-title__text">
        @if (iconName()) {<mat-icon class="page-title__icon">{{ iconName() }}</mat-icon>}
        <span>{{ title }}</span>
      </h2>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .page-title {
      position: relative;
      display: flex; align-items: center; justify-content: center;
      min-height: 48px;
      margin: 8px 16px 12px;
      /* Aire a los lados para la flecha y para la campana de notificaciones. */
      padding: 0 56px;
    }
    .page-title__back { position: absolute; left: 0; top: 50%; transform: translateY(-50%); }
    .page-title__text {
      display: inline-flex; align-items: center; gap: 10px;
      margin: 0; text-align: center;
      font-size: 22px; font-weight: 700; color: #170f5f; line-height: 1.25;
    }
    .page-title__icon {
      flex: 0 0 auto;
      font-size: 28px; width: 28px; height: 28px;
      color: #1010eb;
    }
    @media (max-width: 640px) {
      .page-title { padding: 0 44px; }
      .page-title__text { font-size: 18px; }
      .page-title__icon { font-size: 22px; width: 22px; height: 22px; }
    }
  `],
})
export class PageTitleComponent {
  private readonly router = inject(Router);
  @Input() title = '';
  /** Ruta a la que regresa la flecha; sin ella no se muestra la flecha. */
  @Input() backRoute: string | null = null;
  @Input() backTooltip = 'Regresar';

  /**
   * Icono antes del título. Por omisión es el del módulo (columna
   * `modules.icon`) cuya ruta coincide con la pantalla; se puede forzar.
   */
  @Input() set icon(value: string | null | undefined) { this.iconOverride.set(value ?? null); }
  private readonly iconOverride = signal<string | null>(null);

  private readonly modulesService = inject(ModulesService);
  private readonly activatedRoute = inject(ActivatedRoute);
  /** Ruta real de la pantalla (p. ej. /catalogues/doctors). */
  private readonly path = '/' + this.activatedRoute.snapshot.pathFromRoot
    .map(r => r.url.map(seg => seg.path).join('/'))
    .filter(Boolean)
    .join('/');
  private readonly moduleIcon = this.modulesService.moduleIcon(this.path);
  readonly iconName = computed(() => this.iconOverride() ?? this.moduleIcon());

  back(): void {
    if (this.backRoute) this.router.navigate([this.backRoute]);
  }
}
